import { supabase } from '@/lib/supabase';
import { AppSettingsService } from '@/services/app-settings-service';

export interface CrawlJob {
  id: string;
  symbols: string[];
  pages: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  java_service_url: string;
  total_ideas: number;
  success_count: number;
  skip_count: number;
  old_count: number;
  error_message?: string;
  logs: string[];
  edge_function_logs?: Array<{ timestamp: string; message: string }>;
  last_heartbeat?: string;
  retry_count: number;
  max_retries: number;
  timeout_seconds: number;
  started_at: string;
  completed_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CrawlRequest {
  symbols: string[];
  pages: number;
}

export interface CrawlResult {
  totalSymbols: number;
  totalIdeas: number;
  successCount: number;
  skipCount: number;
  oldCount: number;
  logs: string[];
}

export class TradingViewCrawlerService {
  static async createCrawlJob(request: CrawlRequest): Promise<{ success: boolean; jobId?: string; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      const javaServiceUrl = await AppSettingsService.getJavaServiceUrl();
      
      const { data, error } = await supabase
        .from('crawl_jobs')
        .insert({
          symbols: request.symbols,
          pages: request.pages,
          status: 'pending',
          java_service_url: javaServiceUrl,
          logs: [],
          created_by: user.id
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating crawl job:', error);
        return { success: false, error: error.message };
      }

      return { success: true, jobId: data.id };
    } catch (error) {
      console.error('Error in createCrawlJob:', error);
      return { success: false, error: 'Failed to create crawl job' };
    }
  }

  static async startCrawlJob(jobId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚀 Starting crawl job:', jobId);

      // Get job details
      const { data: job, error: jobError } = await supabase
        .from('crawl_jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (jobError || !job) {
        console.error('Job not found:', jobError);
        return { success: false, error: 'Job not found' };
      }

      console.log('📊 Job details:', {
        symbols: job.symbols,
        pages: job.pages,
        jobId
      });

      // Fire-and-forget call to Edge Function
      // The Edge Function will handle all job status updates
      console.log('📡 Calling Edge Function proxy (fire-and-forget)...');
      
      // Use supabase.functions.invoke without waiting for completion
      supabase.functions.invoke('tradingview-crawler-proxy', {
        body: {
          symbols: job.symbols,
          pages: job.pages,
          crawlJobId: jobId
        }
      }).then((response) => {
        console.log('✅ Edge Function response received:', response);
      }).catch((error) => {
        console.error('❌ Edge Function error (background):', error);
      });

      // Return immediately - the Edge Function handles the rest
      console.log('✅ Crawl job started successfully (fire-and-forget mode)');
      return { success: true };

    } catch (error) {
      console.error('Error in startCrawlJob:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to start crawl job' };
    }
  }

  static async getCrawlJobs(limit: number = 10): Promise<CrawlJob[]> {
    try {
      const { data, error } = await supabase
        .from('crawl_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching crawl jobs:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCrawlJobs:', error);
      return [];
    }
  }

  static async getCrawlJobById(jobId: string): Promise<CrawlJob | null> {
    try {
      const { data, error } = await supabase
        .from('crawl_jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (error) {
        console.error('Error fetching crawl job:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getCrawlJobById:', error);
      return null;
    }
  }

  static async cancelCrawlJob(jobId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('crawl_jobs')
        .update({
          status: 'cancelled',
          completed_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in cancelCrawlJob:', error);
      return { success: false, error: 'Failed to cancel job' };
    }
  }

  static async validateDatabaseConfiguration(): Promise<{ valid: boolean; issues: string[] }> {
    try {
      // Use the secure Edge Function for validation
      const result = await AppSettingsService.validateConfiguration();
      
      return {
        valid: result.success,
        issues: result.issues || []
      };
    } catch (error) {
      console.error('Error validating database configuration:', error);
      return {
        valid: false,
        issues: ['Failed to validate database configuration: ' + (error as Error).message]
      };
    }
  }

  static async getCrawlJobsHealth(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('crawl_jobs_health')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching crawl jobs health:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCrawlJobsHealth:', error);
      return [];
    }
  }

  static async getCrawlJobWithTracking(jobId: string): Promise<CrawlJob | null> {
    try {
      const { data, error } = await supabase
        .from('crawl_jobs')
        .select(`
          *,
          edge_function_logs,
          last_heartbeat,
          retry_count,
          max_retries,
          timeout_seconds
        `)
        .eq('id', jobId)
        .single();

      if (error) {
        console.error('Error fetching crawl job with tracking:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getCrawlJobWithTracking:', error);
      return null;
    }
  }

  static async getUnhealthyJobs(): Promise<CrawlJob[]> {
    try {
      const { data, error } = await supabase
        .from('crawl_jobs_health')
        .select('*')
        .in('health_status', ['stale', 'no_heartbeat'])
        .eq('status', 'running');

      if (error) {
        console.error('Error fetching unhealthy jobs:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUnhealthyJobs:', error);
      return [];
    }
  }

  static subscribeToJobUpdates(jobId: string, callback: (job: CrawlJob) => void) {
    return supabase
      .channel(`crawl_job_${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crawl_jobs',
          filter: `id=eq.${jobId}`
        },
        (payload) => {
          console.log('📡 Real-time job update received:', payload.new);
          callback(payload.new as CrawlJob);
        }
      )
      .subscribe();
  }

  static subscribeToJobUpdatesWithTracking(jobId: string, callback: (job: CrawlJob) => void) {
    return supabase
      .channel(`crawl_job_tracking_${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crawl_jobs',
          filter: `id=eq.${jobId}`
        },
        (payload) => {
          console.log('📡 Real-time job update with tracking received:', {
            ...payload.new,
            edge_function_logs: payload.new.edge_function_logs ? 
              `${payload.new.edge_function_logs.length} logs` : 'no logs',
            last_heartbeat: payload.new.last_heartbeat || 'no heartbeat'
          });
          callback(payload.new as CrawlJob);
        }
      )
      .subscribe();
  }

  static subscribeToJobsList(callback: (jobs: CrawlJob[]) => void) {
    return supabase
      .channel('crawl_jobs_list')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crawl_jobs'
        },
        async () => {
          // Refresh the jobs list when any job changes
          const jobs = await this.getCrawlJobs();
          callback(jobs);
        }
      )
      .subscribe();
  }
}
